import { ref } from "vue";

// Extract filename from Content-Disposition header when present.
// Supports both simple filename="..." and RFC 5987 filename*=UTF-8''... forms.
function getFilenameFromContentDisposition(
  header: string | null
): string | null {
  if (!header) return null;
  try {
    // Prefer RFC 5987 encoding (filename*=)
    const starMatch = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
    if (starMatch && starMatch[1]) {
      const raw = starMatch[1].trim();
      // Values may be percent-encoded and may be quoted
      const unquoted = raw.replace(/^"|"$/g, "");
      try {
        return decodeURIComponent(unquoted);
      } catch {
        return unquoted;
      }
    }
    // Fallback to plain filename=
    const match = header.match(/filename=([^;]+)/i);
    if (match && match[1]) {
      const val = match[1].trim().replace(/^"|"$/g, "");
      return val;
    }
  } catch {
    // ignore parsing errors and let caller fall back
  }
  return null;
}

export function useFileDownloader() {
  const isDownloading = ref(false);
  const error = ref<string>("");

  async function downloadFile(url: string, filename: string) {
    isDownloading.value = true;
    error.value = "";

    try {
      const response = await fetch(url);

      if (response.status != 200) {
        // Try to parse error message from backend
        let message = `Download failed: ${response.status} ${response.statusText}`;
        try {
          const data = await response.json();
          if (data.detail) {
            message = data.detail;
          }
        } catch (e) {
          // Ignore JSON parsing errors, fallback to default message
        }
        error.value = message;
        return;
      }

      // Try to honor server-provided filename first
      const cd =
        response.headers.get("Content-Disposition") ||
        response.headers.get("content-disposition");
      const headerFilename = getFilenameFromContentDisposition(cd);
      const finalFilename = headerFilename || filename;

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = finalFilename;
      document.body.appendChild(a);
      a.click();

      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err: any) {
      error.value = err.message || "Unknown error during download.";
    } finally {
      isDownloading.value = false;
    }
  }

  return {
    isDownloading,
    error,
    downloadFile,
  };
}
